import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ModeStandbyOutlined, PersonOff, People } from "@mui/icons-material";

import { CardLayout, MTable } from "../v3";
import { EmployeeTableColumns } from "../../pages/employee/constants";
import { HumanResourceService } from "../../services";
import { EXPORT_EMPLOYEE } from "../../constants";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";
import { formatExpenseAccountBalance } from "src/utils/formatters";

export const EmployeeList = ({ companyId = null, totalCount = 0, setTotalCount = () => {}, reloadData }) => {
  const [employees, setEmployees] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "employees",
    defaults: { filter: true, sortModel: [{ field: "updatedAt", sort: "desc" }] },
    filterValues: [true, false, null],
  });
  const [loading, setLoading] = useState(true);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasEmployeeExport = useHasPermission(EXPORT_EMPLOYEE);

  const fetchData = useCallback(
    async (employeeFilters = {}) => {
      setLoading(true);
      try {
        const { data: employees } = await HumanResourceService.getEmployees({
          ...employeeFilters,
          type: "EMPLOYEE",
          includeBalance: true,
          ...(companyId !== null && { companyId }),
        });
        const employeesWithFormattedBalance = employees?.rows?.map((employee) => ({
          ...employee,
          account: {
            ...employee.account,
            formattedBalance: employee.account?.balance ? formatExpenseAccountBalance(employee.account.balance) : 0,
          },
        }));
        setEmployees(employeesWithFormattedBalance);
        setTotalCount(employees?.count);
      } catch (error) {
        console.error("🚀 ~ fetchData ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount, companyId],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      active: selectedFilter,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, searchText, reloadData]);

  const handleFilterChange = (value) => {
    const filterValue = value === "null" ? null : JSON.parse(value);
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfTitle = pdfTitleMap[String(selectedFilter)] || "All";
      const pdfColumnHeaders = derivePdfColumns(EmployeeTableColumns);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing
          title={`Employees : ${pdfTitle}`}
          columnHeaders={pdfColumnHeaders}
          tableRows={printableRows}
          company={user?.company}
        />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: `Employees : ${pdfTitle}`,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const onViewClick = (params, event) => {
    navigate(`/employee/${params.row.id}`);
  };

  const filterOptions = [
    { label: "All", value: "null", icon: <ModeStandbyOutlined /> },
    { label: "Active", value: true, icon: <People /> },
    {
      label: "Inactive",
      value: false,
      icon: <PersonOff />,
    },
  ];

  const pdfTitleMap = {
    null: "All",
    true: "Active",
    false: "Inactive",
  };

  const rows = employees
    ? employees?.map((employee, index) => ({
        sl: index + 1 + paginationState.page * paginationState.pageSize,
        id: employee.id,
        name: employee?.name,
        designation: employee?.designation,
        primaryPhone: employee?.primaryPhone,
        salary: employee?.salary,
        balance: employee?.account?.balance,
        formattedBalance: employee?.account?.formattedBalance,
      }))
    : [];
  return (
    <CardLayout>
      <MTable
        // Filter data
        selectedFilter={selectedFilter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        showFilterButton
        // Search data
        placeholder="Search employee"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showSearchBar
        // Export data
        showExportButton={hasEmployeeExport}
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={EmployeeTableColumns}
        tableRows={rows}
        rowCount={totalCount || 0}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
    </CardLayout>
  );
};
